import React, { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button } from '@mui/material';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import {
    PositionModel,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
    LocationModel,
    Side,
    PositionType,
    OptionType,
} from '../../../../../../Generated/Commodity-Management-Api';
import { StoneXDatePicker } from '../../../../../StoneXMui';
import { StoneXGrid, StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { useLoadingState } from '../../../../../../Hooks';
import {
    GrossHedgingReportRow,
    getToolDescription,
    calculatePositionValue,
    calculateCurrentAsset,
    calculateCurrentLiability,
} from './Types';

interface GrossHedgingReportProps {
    customerData?: GetCustomerDataResponse;
    customerSettings?: GetCustomerSettingsResponse;
    defaultQuoteDate?: Date;
}

interface GrossHedgingSummary {
    totalQuantity: number;
    totalCurrentAsset: number;
    totalCurrentLiability: number;
    totalCurrentAssetGainLoss: number;
    totalCurrentLiabilityGainLoss: number;
}

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

const GrossHedgingReport: React.FC<GrossHedgingReportProps> = ({
    customerData,
    defaultQuoteDate,
}) => {
    const loadingState = useLoadingState();
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [quoteDate, setQuoteDate] = useState<Date>(defaultQuoteDate || new Date());
    const [hasRun, setHasRun] = useState<boolean>(false);

    // Default start = first of next month, end = one year from today (match HedgingMTM report)
    const [startMonth, setStartMonth] = useState<number>(dayjs().add(1, 'month').month() + 1);
    const [startYear, setStartYear] = useState<number>(dayjs().add(1, 'month').year());
    const [endMonth, setEndMonth] = useState<number>(dayjs().add(1, 'year').month() + 1);
    const [endYear, setEndYear] = useState<number>(dayjs().add(1, 'year').year());

    const locations: LocationModel[] = customerData?.locations || [];
    const [selectedLocation, setSelectedLocation] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (selectedLocation === undefined && locations.length > 0 && locations[0].locationId !== undefined) {
            setSelectedLocation(locations[0].locationId);
        }
    }, [locations, selectedLocation]);

    const selectedLocationName = locations.find(l => l.locationId === selectedLocation)?.locationName || '';

    const fetchPositions = async () => {
        const response = await CommodityManagementApi.listPositions(
            loadingState.setLoadingState,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            false,
            true,
            true,
            undefined,
            undefined,
            selectedLocation ? [selectedLocation] : undefined,
            undefined,
            undefined,
            undefined,
            startYear,
            startMonth,
            undefined,
            endYear,
            endMonth,
            undefined, // budgetMonthEndFilterLabel
            undefined, // contractMonthStartFilterYear
            undefined, // contractMonthStartFilterMonth
            undefined, // contractMonthStartFilterLabel
            undefined, // contractMonthEndFilterYear
            undefined, // contractMonthEndFilterMonth
            undefined, // contractMonthEndFilterLabel
            undefined, // positionTypesFilter
            undefined, // dealTicketNumberFilter
            undefined, // dealTicketIdFilter
            undefined, // sideFilter
            undefined, // validatedFilter
            undefined, // currencyFilter
            undefined, // currency
            undefined, // expiryStatus
            undefined, // includeAllocations
            undefined, // includeAllocationOffset
            true       // filterByTradeDate - report runs as of the quote date
        );

        setPositions(response.data.rows || []);
        setHasRun(true);
    };

    const handleSubmit = () => {
        fetchPositions();
    };

    const reportRows: GrossHedgingReportRow[] = useMemo(() => {
        const open = positions.filter(p => !p.isExpired);

        // Exclude offsetting pairs: same tenure (year/month), positionType,
        // optionType, strike, commodity, and volume but opposite sides.
        const offsetKey = (p: PositionModel) => [
            p.budgetDate?.year ?? '',
            p.budgetDate?.month ?? '',
            p.positionType ?? '',
            p.optionType ?? '',
            p.optionStrike ?? '',
            p.commodity?.commodityId ?? '',
            p.volume ?? 0,
        ].join('|');

        const excluded = new Set<PositionModel>();
        const sellsByKey = new Map<string, PositionModel[]>();
        open.forEach(p => {
            if (p.side === Side.Sell) {
                const key = offsetKey(p);
                const list = sellsByKey.get(key) ?? [];
                list.push(p);
                sellsByKey.set(key, list);
            }
        });
        open.forEach(p => {
            if (p.side !== Side.Buy) return;
            const key = offsetKey(p);
            const list = sellsByKey.get(key);
            if (list && list.length > 0) {
                const match = list.shift()!;
                excluded.add(p);
                excluded.add(match);
            }
        });

        return open
            .filter(p => !excluded.has(p))
            .map(position => {
                const y = position.budgetDate?.year ?? 0;
                const m = position.budgetDate?.month ?? 1;
                const volume = position.volume ?? 0;
                const price = position.price ?? 0;
                const marketPrice = position.marketPrice ?? 0;
                const sideMultiplier = position.side === Side.Buy ? 1 : -1;
                const mtmValue = (marketPrice - price) * volume * sideMultiplier;
                const isCallOption = position.positionType === PositionType.Option
                    && position.optionType === OptionType.Call;
                return {
                    tenureDate: `${y}-${String(m).padStart(2, '0')}`,
                    formattedDate: dayjs(`${y}-${m}-01`).format('MMM-YYYY'),
                    tool: getToolDescription(position),
                    counterpartyName: position.counterparty?.counterpartyName || '',
                    tradeDate: position.tradeDate ? dayjs(position.tradeDate).format('MM/DD/YYYY') : '',
                    quantity: volume,
                    strike: position.optionStrike ?? 0,
                    price,
                    positionValue: calculatePositionValue(position),
                    mtmValue,
                    commodity: position.commodity?.commodityName || '',
                    dealTicketId: position.dealTicketNumber || position.dealTicketId?.toString() || '',
                    currentAsset: calculateCurrentAsset(position),
                    currentLiability: calculateCurrentLiability(position),
                    currentAssetGainLoss: isCallOption ? mtmValue : 0,
                    currentLiabilityGainLoss: isCallOption ? 0 : mtmValue,
                    position,
                };
            })
            .sort((a, b) => {
                const dateCompare = a.tenureDate.localeCompare(b.tenureDate);
                if (dateCompare !== 0) return dateCompare;
                return a.tradeDate.localeCompare(b.tradeDate);
            });
    }, [positions]);

    const summary: GrossHedgingSummary = useMemo(() => {
        return reportRows.reduce((acc, row) => ({
            totalQuantity: acc.totalQuantity + row.quantity,
            totalCurrentAsset: acc.totalCurrentAsset + row.currentAsset,
            totalCurrentLiability: acc.totalCurrentLiability + row.currentLiability,
            totalCurrentAssetGainLoss: acc.totalCurrentAssetGainLoss + row.currentAssetGainLoss,
            totalCurrentLiabilityGainLoss: acc.totalCurrentLiabilityGainLoss + row.currentLiabilityGainLoss,
        }), {
            totalQuantity: 0,
            totalCurrentAsset: 0,
            totalCurrentLiability: 0,
            totalCurrentAssetGainLoss: 0,
            totalCurrentLiabilityGainLoss: 0,
        });
    }, [reportRows]);

    const fmtCurrency0 = (v: number | null | undefined) =>
        v != null ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '';
    const fmtInt = (v: number | null | undefined) =>
        v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '';

    const columnDefs: (ColDef<GrossHedgingReportRow> | ColGroupDef<GrossHedgingReportRow>)[] = useMemo(() => [
        { field: 'formattedDate', headerName: 'Period', flex: 1, minWidth: 100, pinned: 'left' },
        { field: 'tool', headerName: 'Tool', flex: 1, minWidth: 110 },
        { field: 'counterpartyName', headerName: 'Counterparty', flex: 1.3, minWidth: 150 },
        { field: 'tradeDate', headerName: 'Trade Date', flex: 1, minWidth: 110 },
        {
            field: 'quantity',
            headerName: 'MMBtus Purchased Per Month',
            flex: 1.6,
            minWidth: 180,
            type: 'numericColumn',
            valueFormatter: p => fmtInt(p.value),
        },
        {
            field: 'strike',
            headerName: 'Strike/Fixed Price',
            flex: 1.2,
            minWidth: 140,
            type: 'numericColumn',
            valueFormatter: p => p.value ? `$${p.value.toFixed(3)}` : '',
        },
        {
            field: 'price',
            headerName: 'Purchase Price',
            flex: 1.1,
            minWidth: 130,
            type: 'numericColumn',
            valueFormatter: p => p.value ? `$${p.value.toFixed(4)}` : '',
        },
        {
            headerName: 'Cost of Options',
            children: [
                {
                    field: 'currentAsset',
                    headerName: 'Current Assets',
                    flex: 1.1,
                    minWidth: 130,
                    type: 'numericColumn',
                    valueFormatter: p => fmtCurrency0(p.value),
                },
                {
                    field: 'currentLiability',
                    headerName: 'Current Liabilities',
                    flex: 1.2,
                    minWidth: 140,
                    type: 'numericColumn',
                    valueFormatter: p => fmtCurrency0(p.value),
                },
            ],
        },
        {
            headerName: 'Gain (Loss)',
            children: [
                {
                    field: 'currentAssetGainLoss',
                    headerName: 'Current Assets',
                    flex: 1.1,
                    minWidth: 130,
                    type: 'numericColumn',
                    cellStyle: p => ({ color: (p.value ?? 0) >= 0 ? 'green' : 'red' }),
                    valueFormatter: p => fmtCurrency0(p.value),
                },
                {
                    field: 'currentLiabilityGainLoss',
                    headerName: 'Current Liabilities',
                    flex: 1.2,
                    minWidth: 140,
                    type: 'numericColumn',
                    cellStyle: p => ({ color: (p.value ?? 0) >= 0 ? 'green' : 'red' }),
                    valueFormatter: p => fmtCurrency0(p.value),
                },
            ],
        },
    ], []);

    const handleDownloadExcel = () => {
        if (reportRows.length === 0) return;

        const title = `Gross Hedging Report${selectedLocationName ? ' - ' + selectedLocationName : ''}`;
        const subtitle = `Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`;

        const topHeader = [
            'Period', 'Tool', 'Counterparty', 'Trade Date',
            'MMBtus Purchased Per Month', 'Strike/Fixed Price', 'Purchase Price',
            'Cost of Options', '',
            'Gain (Loss)', '',
        ];
        const subHeader = [
            '', '', '', '', '', '', '',
            'CURRENT ASSETS', 'CURRENT LIABILITIES',
            'CURRENT ASSETS', 'CURRENT LIABILITIES',
        ];

        const aoa: (string | number)[][] = [];
        aoa.push([title]);
        aoa.push([subtitle]);
        aoa.push(topHeader);
        aoa.push(subHeader);

        reportRows.forEach(r => {
            aoa.push([
                r.formattedDate,
                r.tool,
                r.counterpartyName,
                r.tradeDate,
                r.quantity,
                r.strike,
                r.price,
                r.currentAsset,
                r.currentLiability,
                r.currentAssetGainLoss,
                r.currentLiabilityGainLoss,
            ]);
        });

        aoa.push([
            'SUMMARY:', '', '', '',
            summary.totalQuantity,
            '', '',
            summary.totalCurrentAsset,
            summary.totalCurrentLiability,
            summary.totalCurrentAssetGainLoss,
            summary.totalCurrentLiabilityGainLoss,
        ]);

        const sheet = XLSX.utils.aoa_to_sheet(aoa);
        const lastCol = topHeader.length - 1;
        sheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
            { s: { r: 2, c: 7 }, e: { r: 2, c: 8 } },
            { s: { r: 2, c: 9 }, e: { r: 2, c: 10 } },
        ];

        const currency0 = '"$"#,##0;[Red]("$"#,##0)';
        const currency3 = '"$"#,##0.000;[Red]("$"#,##0.000)';
        const currency4 = '"$"#,##0.0000;[Red]("$"#,##0.0000)';
        const intFmt = '#,##0;(#,##0)';

        const dataStart = 4;
        for (let r = dataStart; r < aoa.length; r++) {
            const apply = (c: number, fmt: string) => {
                const ref = XLSX.utils.encode_cell({ r, c });
                const cell = sheet[ref];
                if (cell && typeof cell.v === 'number') {
                    cell.t = 'n';
                    cell.z = fmt;
                }
            };
            apply(4, intFmt);
            apply(5, currency3);
            apply(6, currency4);
            apply(7, currency0);
            apply(8, currency0);
            apply(9, currency0);
            apply(10, currency0);
        }

        sheet['!cols'] = [
            { wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 12 },
            { wch: 24 }, { wch: 16 }, { wch: 14 },
            { wch: 16 }, { wch: 18 },
            { wch: 16 }, { wch: 18 },
        ];

        const workbook = XLSX.utils.book_new();
        const sheetName = (selectedLocationName || 'Gross').replace(/[\\/*?:[\]]/g, '').substring(0, 31) || 'Gross';
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName);

        const filename = `GrossHedgingReport_${dayjs(quoteDate).format('YYYYMMDD')}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Gross Hedging Report</Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <StoneXRow>
                    <StoneXDatePicker
                        label="Marking to Market Date"
                        value={quoteDate}
                        onChange={(date) => setQuoteDate(date || new Date())}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Start Month</InputLabel>
                        <Select
                            value={startMonth}
                            label="Start Month"
                            onChange={(e) => setStartMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Start Year"
                        type="number"
                        size="small"
                        value={startYear}
                        onChange={(e) => setStartYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>End Month</InputLabel>
                        <Select
                            value={endMonth}
                            label="End Month"
                            onChange={(e) => setEndMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="End Year"
                        type="number"
                        size="small"
                        value={endYear}
                        onChange={(e) => setEndYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Location</InputLabel>
                        <Select
                            value={selectedLocation ?? ''}
                            label="Location"
                            onChange={(e) => setSelectedLocation(Number(e.target.value))}
                        >
                            {locations.map((l: LocationModel) => (
                                <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </StoneXRow>
                <StoneXRow>
                    <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                    <Button
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        disabled={!hasRun || reportRows.length === 0}
                        sx={{ ml: 1 }}
                    >
                        Save to Excel
                    </Button>
                </StoneXRow>
            </Paper>

            {loadingState.isLoading() ? (
                <StoneXLoading show />
            ) : hasRun ? (
                <>
                    <StoneXGrid
                        columns={columnDefs as ColDef<GrossHedgingReportRow>[]}
                        rows={reportRows}
                    />

                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="h6">Summary</Typography>
                        <Box sx={{ display: 'flex', gap: 4, mt: 1, flexWrap: 'wrap' }}>
                            <Typography>
                                <strong>Total MMBtus:</strong> {fmtInt(summary.totalQuantity)}
                            </Typography>
                            <Typography>
                                <strong>Cost of Options - Current Assets:</strong> {fmtCurrency0(summary.totalCurrentAsset)}
                            </Typography>
                            <Typography>
                                <strong>Cost of Options - Current Liabilities:</strong> {fmtCurrency0(summary.totalCurrentLiability)}
                            </Typography>
                            <Typography>
                                <strong>Gain (Loss) - Current Assets:</strong>
                                <span style={{ color: summary.totalCurrentAssetGainLoss >= 0 ? 'green' : 'red' }}>
                                    {' '}{fmtCurrency0(summary.totalCurrentAssetGainLoss)}
                                </span>
                            </Typography>
                            <Typography>
                                <strong>Gain (Loss) - Current Liabilities:</strong>
                                <span style={{ color: summary.totalCurrentLiabilityGainLoss >= 0 ? 'green' : 'red' }}>
                                    {' '}{fmtCurrency0(summary.totalCurrentLiabilityGainLoss)}
                                </span>
                            </Typography>
                        </Box>
                    </Paper>
                </>
            ) : null}
        </Box>
    );
};

export default GrossHedgingReport;
